import {
  ATTENDANCE_STATUS,
  TAttendanceByDateRecord,
  IAttendanceByDateEntry,
  IAttendanceSummaryMetrics,
} from 'src/apis/__shared/attendance.interfaces';
import { mockUserDetails, mockStudentUserDetails } from '../users/userDetailsMock';
import { mockUserAction } from '../users/userAction';

// Helper function to create attendance record
const createAttendanceRecord = (
  attendanceId: string,
  userDetails: typeof mockUserDetails.basicDetails,
  status: ATTENDANCE_STATUS,
  comment?: string
): TAttendanceByDateRecord => ({
  attendanceId,
  userDetails,
  attendanceStatus: status,
  comment,
  actions: {
    created: mockUserAction,
    updated: mockUserAction,
  },
});

// Mock attendance records for different dates
const createDateEntry = (
  date: string,
  records: TAttendanceByDateRecord[]
): IAttendanceByDateEntry => {
  const totalPresent = records.filter(
    (r) => r.attendanceStatus === ATTENDANCE_STATUS.PRESENT
  ).length;
  const totalLate = records.filter(
    (r) => r.attendanceStatus === ATTENDANCE_STATUS.LATE
  ).length;
  const totalAbsent = records.filter(
    (r) => r.attendanceStatus === ATTENDANCE_STATUS.ABSENT
  ).length;
  const totalHalfDay = records.filter(
    (r) => r.attendanceStatus === ATTENDANCE_STATUS.HALF_DAY
  ).length;
  const totalHoliday = records.filter(
    (r) => r.attendanceStatus === ATTENDANCE_STATUS.HOLIDAY
  ).length;
  const totalLateWithExcuse = records.filter(
    (r) => r.attendanceStatus === ATTENDANCE_STATUS.LATE_WITH_EXCUSE
  ).length;
  const totalDays = records.length;
  const attendanceRate =
    totalDays > 0 ? ((totalPresent / totalDays) * 100).toFixed(1) : '0';

  const summary: IAttendanceSummaryMetrics = {
    totalPresent,
    totalLate,
    totalAbsent,
    totalHalfDay,
    totalHoliday,
    totalLateWithExcuse,
    attendanceRate: parseFloat(attendanceRate),
    tardinessRate: totalDays > 0 ? parseFloat(((totalLate / totalDays) * 100).toFixed(1)) : 0,
    excusedAbsentRate: totalDays > 0
      ? parseFloat(((totalLateWithExcuse / totalDays) * 100).toFixed(1))
      : 0,
    unexcusedAbsentRate: totalDays > 0
      ? parseFloat(((totalAbsent / totalDays) * 100).toFixed(1))
      : 0,
  };

  return {
    date,
    records,
    summary,
  };
};

// Legacy format: array of records (for backward compatibility)
export const mockAttendanceByDate: TAttendanceByDateRecord[] = [
  createAttendanceRecord('1', mockUserDetails.basicDetails, ATTENDANCE_STATUS.PRESENT, 'Present'),
  createAttendanceRecord('2', mockStudentUserDetails.basicDetails, ATTENDANCE_STATUS.ABSENT, 'Absent'),
  createAttendanceRecord('3', mockUserDetails.basicDetails, ATTENDANCE_STATUS.LATE, 'Late'),
  createAttendanceRecord('4', mockStudentUserDetails.basicDetails, ATTENDANCE_STATUS.PRESENT, 'Present'),
  createAttendanceRecord('5', mockUserDetails.basicDetails, ATTENDANCE_STATUS.HALF_DAY, 'Half Day'),
  createAttendanceRecord('6', mockStudentUserDetails.basicDetails, ATTENDANCE_STATUS.HOLIDAY, 'Holiday'),
  createAttendanceRecord('7', mockUserDetails.basicDetails, ATTENDANCE_STATUS.PRESENT, 'Present'),
  createAttendanceRecord('8', mockStudentUserDetails.basicDetails, ATTENDANCE_STATUS.LATE_WITH_EXCUSE, 'Late with excuse'),
  createAttendanceRecord('9', mockUserDetails.basicDetails, ATTENDANCE_STATUS.ABSENT, 'Absent'),
  createAttendanceRecord('10', mockStudentUserDetails.basicDetails, ATTENDANCE_STATUS.PRESENT, 'Present'),
  createAttendanceRecord('11', mockUserDetails.basicDetails, ATTENDANCE_STATUS.PRESENT, 'Present'),
  createAttendanceRecord('12', mockStudentUserDetails.basicDetails, ATTENDANCE_STATUS.LATE, 'Late'),
];

// New format: array of date entries with summary metrics
export const mockAttendanceByDateEntries: IAttendanceByDateEntry[] = [
  createDateEntry('2024-01-01', [
    createAttendanceRecord('1', mockUserDetails.basicDetails, ATTENDANCE_STATUS.PRESENT, 'Present'),
    createAttendanceRecord('2', mockStudentUserDetails.basicDetails, ATTENDANCE_STATUS.PRESENT, 'Present'),
  ]),
  createDateEntry('2024-01-02', [
    createAttendanceRecord('3', mockUserDetails.basicDetails, ATTENDANCE_STATUS.ABSENT, 'Absent'),
    createAttendanceRecord('4', mockStudentUserDetails.basicDetails, ATTENDANCE_STATUS.LATE, 'Late'),
  ]),
  createDateEntry('2024-01-03', [
    createAttendanceRecord('5', mockUserDetails.basicDetails, ATTENDANCE_STATUS.PRESENT, 'Present'),
    createAttendanceRecord('6', mockStudentUserDetails.basicDetails, ATTENDANCE_STATUS.HALF_DAY, 'Half Day'),
  ]),
  createDateEntry('2024-01-04', [
    createAttendanceRecord('7', mockUserDetails.basicDetails, ATTENDANCE_STATUS.HOLIDAY, 'Holiday'),
    createAttendanceRecord('8', mockStudentUserDetails.basicDetails, ATTENDANCE_STATUS.HOLIDAY, 'Holiday'),
  ]),
  createDateEntry('2024-01-05', [
    createAttendanceRecord('9', mockUserDetails.basicDetails, ATTENDANCE_STATUS.PRESENT, 'Present'),
    createAttendanceRecord('10', mockStudentUserDetails.basicDetails, ATTENDANCE_STATUS.LATE_WITH_EXCUSE, 'Late with excuse'),
  ]),
];
